import moment from 'moment';
import { formatMonthTitleCN, isGreaterChinaLocale } from '@marriott/shared/mi-helper-utils';
import type { DateObjectType } from '../../../utils/DateUtils';
import { FLEXIBLE_FORMAT_YEAR_MONTH } from '../../../../../../constants/Calendar.constant';
import { getLocalizedDate } from '@marriott/mi-ui-library';
import { convertToUpperCase } from '../../../../../../../src/utils/src/utils';
import { formatDateStringBasedOnLocale } from '../../../../../../utils/src/helper';
import { AR_LOCALE, HI_LOCALE } from '../../../../../../modules/constants/Calendar.constants';

/**
 * Generates the display text based on selected dates, locale, and page type.
 *
 * @param {string} datesPlaceholderText - The placeholder text for the date range.
 * @param {DateObjectType | undefined} fromDate - The start date of the range.
 * @param {DateObjectType | undefined} toDate - The end date of the range.
 * @param {boolean} isSearchPage - Indicates if the current page is the search page.
 * @param {boolean} showDatesOnMobile - Determines whether dates should be shown on mobile.
 * @param {string} tabSelected - The currently selected tab.
 * @param {string} flexibleTabText - The text representing the flexible tab option.
 * @param {string} currentLocale - The current locale of the user.
 * @param {number} monthSelected - The selected month (0-based index).
 * @param {number} yearSelected - The selected year.
 * @param {function} getLocalizedDate - A function that localizes a date.
 *
 * @returns {string} The formatted display text.
 */
export const getDateText = (
  datesPlaceholderText: string,
  fromDate: DateObjectType | undefined,
  toDate: DateObjectType | undefined,
  isSearchPage: boolean,
  showDatesOnMobile: boolean,
  tabSelected: string,
  flexibleTabText: string,
  currentLocale: string,
  monthSelected: number,
  yearSelected: number,
  dateFormatInput?: string
): string => {
  if (!(showDatesOnMobile || isSearchPage)) {
    return datesPlaceholderText;
  }

  let displayText = datesPlaceholderText;

  if (fromDate || toDate) {
    displayText = `${getLocalizedDate(fromDate, dateFormatInput, currentLocale)}${
      toDate ? ` - ${getLocalizedDate(toDate, dateFormatInput, currentLocale)}` : ''
    }`;
  }

  if (isSearchPage && tabSelected === flexibleTabText) {
    // Adding 1 as the default 'day' value since we only use month and year for flex date scenarios anyway
    const localizedDate = getLocalizedDate(
      moment(new Date(yearSelected ?? new Date().getFullYear(), monthSelected, 1)),
      dateFormatInput ?? FLEXIBLE_FORMAT_YEAR_MONTH,
      currentLocale
    );
    const returnedDate = convertToUpperCase(localizedDate, currentLocale);
    displayText = isGreaterChinaLocale(currentLocale)
      ? formatMonthTitleCN({ month: monthSelected, year: yearSelected })
      : returnedDate;
  }
  displayText = formatDateStringBasedOnLocale(displayText);
  return displayText;
};
